import {
  formatLinguaMessage,
  LinguaCatalogRuntime,
  markOccurrence,
  type BundledNamespace,
  type LinguaDeploymentDiscovery,
  type LinguaMessageValue,
  type OccurrenceMetadata,
} from "@fi-solutions/lingua-runtime-vue";
import { ref } from "vue";
import { defaultLanguage } from "@/language";

const APP_NAMESPACE = "app";
const BDE_NAMESPACE = "bde";
// Extension previews update the editing tab immediately. Focus and reload cover ordinary
// navigation; this timer is only a slow recovery path when neither happens.
const LINGUA_REVALIDATE_MILLISECONDS = 24 * 60 * 60 * 1_000;

const bundledCatalogs = import.meta.glob<Record<string, string>>(
  "../translations/{app,bde}/*.json",
  { import: "default" },
);

let lingua: LinguaCatalogRuntime | undefined;
let stopPublicationNotifications: (() => void) | undefined;
export const linguaRevision = ref(0);
const TRANSLATION_PREVIEW_EVENT = "lingua-extension-translation-preview";
const previewTranslations = new Map<string, string>();
let activeDiscovery: LinguaDeploymentDiscovery | undefined;
type CatalogDiscovery = LinguaDeploymentDiscovery & { catalogOrigin?: string };

interface TranslationPreview {
  project: string;
  target: string;
  keyId: string;
  namespace: string;
  key: string;
  locale: string;
  value: string;
}

function previewKey(locale: string, namespace: string, key: string): string {
  return `${locale}\u0000${namespace}\u0000${key}`;
}

function isTranslationPreview(value: unknown): value is TranslationPreview {
  if (!value || typeof value !== "object") return false;
  const detail = value as Record<string, unknown>;
  return ["project", "target", "keyId", "namespace", "key", "locale", "value"].every(
    (field) => typeof detail[field] === "string",
  );
}

function onTranslationPreview(event: Event): void {
  const preview = (event as CustomEvent<unknown>).detail;
  if (!lingua || !activeDiscovery || !isTranslationPreview(preview)) return;
  const occurrence = lingua.occurrence(preview.namespace, preview.key);
  if (
    preview.project !== activeDiscovery.project ||
    preview.target !== activeDiscovery.target?.id ||
    occurrence?.keyId !== preview.keyId
  )
    return;
  previewTranslations.set(
    previewKey(preview.locale, preview.namespace, preview.key),
    preview.value,
  );
  if (preview.locale === lingua.getLocale()) linguaRevision.value += 1;
}

function normalizedLocale(locale: string | undefined): string {
  return locale?.replaceAll("_", "-") ?? defaultLanguage;
}

function validateDiscovery(discovery: CatalogDiscovery): void {
  if (discovery.project !== "frontend") {
    throw new Error(`Expected Lingua project frontend, received ${discovery.project}`);
  }
  if (
    discovery.target &&
    (!["branch", "release"].includes(discovery.target.type) || !discovery.target.id.trim())
  ) {
    throw new Error("Lingua deployment target is missing or invalid");
  }
  const serviceOrigin = new URL(discovery.serviceOrigin);
  if (
    !["http:", "https:"].includes(serviceOrigin.protocol) ||
    serviceOrigin.origin !== discovery.serviceOrigin.replace(/\/$/, "")
  ) {
    throw new Error("Lingua serviceOrigin must be an HTTP(S) origin without a path");
  }
  if (discovery.catalogOrigin) {
    const catalogOrigin = new URL(discovery.catalogOrigin);
    if (
      !["http:", "https:"].includes(catalogOrigin.protocol) ||
      catalogOrigin.search ||
      catalogOrigin.hash ||
      catalogOrigin.username ||
      catalogOrigin.password
    ) {
      throw new Error(
        "Lingua catalogOrigin must be an HTTP(S) base URL without credentials, query, or fragment",
      );
    }
  }
}

export async function discoverFrontendLinguaDeployment(): Promise<CatalogDiscovery> {
  const basePath = import.meta.env.BASE_URL.endsWith("/")
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
  const response = await fetch(
    new URL(`${basePath}.well-known/lingua.json`, window.location.origin),
    { credentials: "omit", cache: "no-cache" },
  );
  if (!response.ok) {
    throw new Error(`Lingua deployment discovery failed (${response.status})`);
  }
  const discovery = (await response.json()) as CatalogDiscovery;
  validateDiscovery(discovery);
  return discovery;
}

async function loadBundledCatalog(
  locale: string,
  namespace: string,
): Promise<BundledNamespace | undefined> {
  const loader = bundledCatalogs[`../translations/${namespace}/${normalizedLocale(locale)}.json`];
  if (!loader) return undefined;
  return { catalog: await loader() };
}

function reportLinguaDeliveryError(error: unknown): void {
  // Delivery is non-fatal because the build ships immutable fallback catalogs.
  // eslint-disable-next-line no-console
  console.error("Failed to refresh Lingua translations; using the last valid catalog", error);
}

function startPublicationNotifications(
  runtime: LinguaCatalogRuntime,
  discovery: LinguaDeploymentDiscovery,
): () => void {
  if (typeof EventSource === "undefined") return () => undefined;
  let stream: EventSource;
  try {
    const url = new URL("/api/v1/catalog-updates", discovery.serviceOrigin);
    url.searchParams.set("project", discovery.project);
    stream = new EventSource(url.href);
  } catch (error) {
    reportLinguaDeliveryError(error);
    return () => undefined;
  }

  let stopped = false;
  let refreshing = false;
  let queued = false;
  const revalidate = async (): Promise<void> => {
    if (refreshing) {
      queued = true;
      return;
    }
    refreshing = true;
    do {
      queued = false;
      try {
        await runtime.refresh();
      } catch (error) {
        reportLinguaDeliveryError(error);
      }
    } while (queued && !stopped);
    refreshing = false;
  };
  // A publication may happen between the startup fetch and the stream opening.
  stream.onopen = () => void revalidate();
  stream.onmessage = (event) => {
    if (event.data === "published") void revalidate();
  };
  return () => {
    stopped = true;
    stream.close();
  };
}

export async function createLinguaRuntime(initialLanguage?: string): Promise<LinguaCatalogRuntime> {
  const discovered = await discoverFrontendLinguaDeployment();
  // Compatibility fields can be removed after the reduced discovery contract is published in the
  // runtime package. They are derived, not deployment configuration.
  const discovery = {
    ...discovered,
    applicationVersion: import.meta.env.__VERSION__,
    buildRevision: import.meta.env.__VERSION__,
    environment: import.meta.env.MODE,
  };
  validateDiscovery(discovery);
  activeDiscovery = discovery;
  lingua = new LinguaCatalogRuntime({
    discovery,
    locale: normalizedLocale(initialLanguage),
    namespaces: [APP_NAMESPACE],
    revalidateMilliseconds: LINGUA_REVALIDATE_MILLISECONDS,
    bundledCatalogLoader: loadBundledCatalog,
    onUpdate: () => {
      previewTranslations.clear();
      linguaRevision.value += 1;
    },
    onDeliveryError: reportLinguaDeliveryError,
  });
  await lingua.start();
  document.addEventListener(TRANSLATION_PREVIEW_EVENT, onTranslationPreview);
  stopPublicationNotifications?.();
  stopPublicationNotifications = startPublicationNotifications(lingua, discovery);
  return lingua;
}

export function stopLinguaRuntime(): void {
  stopPublicationNotifications?.();
  stopPublicationNotifications = undefined;
  document.removeEventListener(TRANSLATION_PREVIEW_EVENT, onTranslationPreview);
  previewTranslations.clear();
  activeDiscovery = undefined;
  lingua?.stop();
  lingua = undefined;
}

export function changeLinguaLanguage(language: string): Promise<boolean> {
  if (!lingua) throw new Error("Lingua must be initialized before changing its language");
  return lingua.changeLocale(normalizedLocale(language));
}

interface TranslationArguments {
  values: Record<string, LinguaMessageValue>;
  defaultValue?: string;
}

function translationArguments(arguments_: unknown[]): TranslationArguments {
  const values: Record<string, LinguaMessageValue> = {};
  let defaultValue: string | undefined;
  for (const [position, argument] of arguments_.entries()) {
    if (argument === undefined) continue;
    if (typeof argument === "number") {
      values.count = argument;
      continue;
    }
    if (typeof argument === "string") {
      if (position === 0) defaultValue = argument;
      continue;
    }
    if (Array.isArray(argument)) {
      Object.assign(values, Object.fromEntries(argument.map((value, index) => [index, value])));
      continue;
    }
    if (argument === null || typeof argument !== "object") continue;
    const {
      locale: _locale,
      plural,
      named,
      list,
      default: argumentDefault,
      ...namedValues
    } = argument as Record<string, unknown>;
    if (typeof plural === "number") values.count = plural;
    if (typeof argumentDefault === "string") defaultValue = argumentDefault;
    if (named && typeof named === "object" && !Array.isArray(named)) Object.assign(values, named);
    if (Array.isArray(list)) {
      Object.assign(values, Object.fromEntries(list.map((value, index) => [index, value])));
    }
    Object.assign(values, namedValues);
  }
  return { values: values as Record<string, LinguaMessageValue>, defaultValue };
}

export function translateLingua(namespace: string, key: string, ...arguments_: unknown[]): string {
  if (!lingua) throw new Error("Lingua must be initialized before translating application keys");
  const { values, defaultValue } = translationArguments(arguments_);
  const preview = previewTranslations.get(previewKey(lingua.getLocale(), namespace, key));
  if (preview !== undefined) {
    const formatted = formatLinguaMessage(preview, lingua.getLocale(), values);
    const occurrence = lingua.occurrence(namespace, key);
    return occurrence && document.documentElement.dataset.linguaExtensionActive === "true"
      ? markOccurrence(formatted, { ...occurrence, status: "direct" })
      : formatted;
  }
  if (!lingua.has(namespace, key) && defaultValue !== undefined) return defaultValue;
  return lingua.translate(namespace, key, values);
}

export function hasLinguaTranslation(namespace: string, key: string): boolean {
  if (!lingua) return false;
  return (
    previewTranslations.has(previewKey(lingua.getLocale(), namespace, key)) ||
    lingua.has(namespace, key)
  );
}

export function getLinguaTranslationsByPrefix(
  namespace: string,
  prefix: string,
): Record<string, string> {
  if (!lingua) return {};
  const entries = lingua.entries(namespace, prefix);
  const previewPrefix = `${lingua.getLocale()}\u0000${namespace}\u0000`;
  for (const [compositeKey, value] of previewTranslations) {
    if (!compositeKey.startsWith(previewPrefix)) continue;
    const key = compositeKey.slice(previewPrefix.length);
    if (key.startsWith(prefix)) entries[key] = value;
  }
  return entries;
}

export function getLinguaMessage(namespace: string, key: string): string | undefined {
  if (!lingua) return undefined;
  return (
    previewTranslations.get(previewKey(lingua.getLocale(), namespace, key)) ??
    lingua.message(namespace, key)
  );
}

export function getLinguaOccurrence(
  namespace: string,
  key: string,
): OccurrenceMetadata | undefined {
  return lingua?.occurrence(namespace, key);
}

export async function activateLinguaNamespace(namespace: string): Promise<void> {
  if (!lingua) throw new Error("Lingua must be initialized before loading a namespace");
  await lingua.useNamespace(namespace);
}

export { APP_NAMESPACE, BDE_NAMESPACE };
